import { Fragment, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import backend from '../../services/backend';
import { Link } from '../../components/Link';
import {
  appendSnapshot,
  overallState,
  stageTotals,
  STATE_BADGE,
  historicalRatesByStage,
  estimateGlobalEtaMinutes,
  formatEta,
  SNAPSHOT_WINDOW_MS,
  type Snapshot,
} from './helpers';
import PipelineLanes from './PipelineLanes';
import ThroughputChart from './ThroughputChart';
import InstancesChart from './InstancesChart';
import { Badge } from '../../catalyst/badge';
import { onPipelineStageIds, pipelineLanes } from './pipelineLayout';

const REFRESH_INTERVAL = 5_000;

export default function Monitoring() {
  const historyRef = useRef<Snapshot[]>([]);
  const { data, error } = useQuery({
    queryKey: ['pipelineStatus'],
    queryFn: () => backend.getPipelineStatus().then((res) => res.data),
    refetchInterval: REFRESH_INTERVAL,
  });

  // Accumulated during render on purpose: appendSnapshot is idempotent per
  // generatedAt, and the query's data identity change already triggers the
  // re-render — a setState-in-effect would be a lint violation for no gain.
  if (data) {
    // eslint-disable-next-line react-hooks/refs
    historyRef.current = appendSnapshot(historyRef.current, data);
  }

  const stages = data?.stages ?? [];
  const state = STATE_BADGE[overallState(stages)];
  const totals = stageTotals(stages);
  // eslint-disable-next-line react-hooks/refs
  const history = historyRef.current;
  const avgRates = historicalRatesByStage(history);
  const onPipelineIds = onPipelineStageIds(pipelineLanes(data?.controlPlane));
  const etaMinutes = estimateGlobalEtaMinutes(
    stages.filter((s) => onPipelineIds.has(s.id)),
    avgRates,
  );
  const windowMinutesLabel = `${SNAPSHOT_WINDOW_MS / 60_000} min`;
  // DB-side wedges: documents the queue stats cannot see at all. `capped` means
  // candidate discovery exhausted its scan budget — the count is exact for the
  // scanned window only, and further stalls may exist even when count is 0.
  const stalled = data?.stalledDocuments;
  const stalledExtra = stalled ? stalled.count - stalled.documentIds.length : 0;
  const showStalled = stalled !== undefined && (stalled.count > 0 || stalled.capped === true);

  return (
    <div className="p-4 flex flex-col gap-6">
      <div className="flex items-center gap-3 flex-wrap">
        <h1 className="text-lg font-medium text-gray-900 dark:text-zinc-100">Pipeline Monitoring</h1>
        {data && <Badge color={state.color}>{state.label}</Badge>}
        <span className="text-sm text-gray-500 dark:text-zinc-400">
          {totals.queued} queued · {totals.ratePerMin}/min · {totals.instances} instances
        </span>
        {data && (
          // Gated on etaMinutes (already scoped to on-pipeline stages), not totals.queued
          // (includes off-pipeline other/report) — otherwise a clear pipeline with only
          // off-pipeline backlog would render the contradictory "done remaining".
          <Badge color={etaMinutes === 0 ? 'zinc' : etaMinutes === null ? 'zinc' : 'sky'}>
            {etaMinutes === 0 ? 'Nothing queued' : etaMinutes === null ? 'ETA unknown' : `${formatEta(etaMinutes)} remaining`}
          </Badge>
        )}
        {showStalled &&
          // Red only for CONFIRMED stalls; a capped scan that found none is an
          // amber "coverage gap" notice, not an alarm.
          (stalled.count > 0 ? (
            <Badge color="red">
              {stalled.count}
              {stalled.capped ? '+' : ''} stalled
            </Badge>
          ) : (
            <Badge color="amber">stall scan capped</Badge>
          ))}
      </div>
      {showStalled &&
        (stalled.count > 0 ? (
          <div className="text-sm text-red-600 dark:text-red-400">
            {stalled.count}
            {stalled.capped ? '+' : ''} document{stalled.count === 1 && !stalled.capped ? ' is' : 's are'} stalled in
            the pipeline with no recent processing progress —{' '}
            {stalled.documentIds.map((id, index) => (
              <Fragment key={id}>
                {index > 0 && ', '}
                <Link to={`/documents/${id}`}>{id.slice(0, 8)}</Link>
              </Fragment>
            ))}
            {stalledExtra > 0 && ` and ${stalledExtra} more`}
            {stalled.capped && ' (scan budget hit — further stalls may exist)'}
          </div>
        ) : (
          // capped with an in-budget count of 0: the scan proved nothing.
          <div className="text-sm text-amber-600 dark:text-amber-400">
            Stalled-document scan hit its budget with no stalls found in the scanned window — coverage is incomplete.
          </div>
        ))}
      {error && !data ? (
        <div className="text-sm text-red-600 dark:text-red-400">Failed to load pipeline status.</div>
      ) : (
        <>
          {error && (
            // Transient refresh failure — keep showing the last good snapshot
            <div className="text-sm text-amber-600 dark:text-amber-400">
              Refresh failed — showing the last loaded snapshot.
            </div>
          )}
          <div className="rounded-lg border border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 dark:shadow-zinc-950/30 p-4">
            <PipelineLanes stages={stages} avgRates={avgRates} controlPlane={data?.controlPlane} />
          </div>
          <div className="rounded-lg border border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 dark:shadow-zinc-950/30 p-4">
            <h2 className="text-sm font-medium text-gray-900 dark:text-zinc-100 mb-3">
              Throughput (this session, last {windowMinutesLabel})
            </h2>
            <ThroughputChart history={history} />
          </div>
          <div className="rounded-lg border border-gray-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 dark:shadow-zinc-950/30 p-4">
            <h2 className="text-sm font-medium text-gray-900 dark:text-zinc-100 mb-3">
              Instances (this session, last {windowMinutesLabel})
            </h2>
            <InstancesChart history={history} />
          </div>
        </>
      )}
    </div>
  );
}
